import { compareCards } from "@/assets/functions/cardNumber.js"

/**
 * LE BOOSTER FULL : une collection entière, d'un seul coup.
 *
 * ---------------------------------------------------------------------------
 * CE QUE C'EST
 * ---------------------------------------------------------------------------
 *
 * Un booster ordinaire tire au sort : une taille, des raretés, des variantes.
 * Un booster Full ne tire rien. Il contient TOUTE la collection qu'il vise,
 * extensions comprises, à un exemplaire par carte et toujours en variante
 * classique. Sa « taille » n'est donc pas un réglage : c'est une conséquence,
 * recomptée à partir du catalogue.
 *
 * ---------------------------------------------------------------------------
 * POURQUOI LA RÈGLE VIT ICI ET NULLE PART AILLEURS
 * ---------------------------------------------------------------------------
 *
 * Trois écrans ont besoin de la même réponse, et aucun ne la connaît par
 * lui-même : l'administration l'affiche en remplissant le champ « Taille »,
 * l'ouverture la distribue, et la boutique en annonce le nombre. Trois copies
 * de « toutes les cartes de la collection, extensions publiques comprises »
 * seraient trois occasions de ne plus dire la même chose — et le jour où elles
 * divergent, un joueur paie pour un nombre de cartes qu'il ne reçoit pas.
 *
 * ---------------------------------------------------------------------------
 * LES EXTENSIONS, ET CELLES QU'ON NE MONTRE PAS
 * ---------------------------------------------------------------------------
 *
 * « Avec toutes ses extensions » s'entend des extensions PUBLIQUES. Une
 * extension non publique est une extension que personne n'est censé avoir vue ;
 * la livrer dans un booster la révélerait, et c'est déjà la règle que suit
 * l'ouverture ordinaire (voir `views/Boosters.vue`). Elle entrera dans les
 * boosters Full le jour où elle sera publiée, sans que rien ne soit à
 * reconfigurer : la taille se recompte.
 *
 * UN BOOSTER FULL VISE DES COLLECTIONS, PAS DES EXTENSIONS. Choisir une
 * extension seule n'aurait pas de sens pour un objet dont la définition est
 * « toute la collection » ; si l'administration n'a renseigné que des
 * extensions, on remonte à leurs collections et on prend le tout.
 */

/** Vrai pour un booster Full. Un ancien booster n'a pas le champ : il est faux. */
export function isFullBooster(booster) {
	return booster?.isFull === true
}

/**
 * Les collections qu'un booster vise, directement ou par ses extensions.
 *
 * @param {object} booster
 * @param {Array} extensions Toutes les extensions connues.
 * @returns {string[]}
 */
export function targetedCollectionIds(booster, extensions = []) {
	const direct = Array.isArray(booster?.targetCollections) ? booster.targetCollections : []
	const viaExtensions = (Array.isArray(booster?.targetExtensions) ? booster.targetExtensions : [])
		.map((extId) => extensions.find((e) => e.id === extId)?.collection)
		.filter(Boolean)

	// Le champ `collection` est l'ancien format : il ne sert que si rien d'autre
	// n'est renseigné, exactement comme partout ailleurs dans l'application.
	const all = [...direct, ...viaExtensions]
	if (all.length === 0 && booster?.collection) all.push(booster.collection)

	return [...new Set(all)]
}

/**
 * Les extensions qu'un booster Full emporte : toutes les publiques de ses
 * collections.
 *
 * Écrites dans `targetExtensions` à l'enregistrement, de sorte que tous les
 * écrans qui lisent déjà ce champ — l'accès à l'inventaire, le filtre de
 * l'ouverture — fonctionnent sans rien savoir des boosters Full.
 */
export function fullExtensionIds(booster, extensions = []) {
	const collections = targetedCollectionIds(booster, extensions)
	return extensions
		.filter((ext) => ext.isPublic !== false && collections.includes(ext.collection))
		.map((ext) => ext.id)
}

/**
 * LE CONTENU EXACT d'un booster Full, dans l'ordre où on le lit : par
 * collection, puis par numéro de carte.
 *
 * @param {object} booster
 * @param {{cards: Array, extensions: Array, collections?: Array}} catalogue
 * @returns {Array} Les cartes, une fois chacune.
 */
export function fullBoosterCards(booster, { cards = [], extensions = [], collections = [] } = {}) {
	const collectionIds = targetedCollectionIds(booster, extensions)
	if (collectionIds.length === 0) return []

	const openExtensions = new Set(fullExtensionIds(booster, extensions))

	const kept = cards.filter((card) => {
		if (!collectionIds.includes(card.collection)) return false
		// Carte de base : elle appartient à la collection, donc au booster.
		if (!card.extension) return true
		// Carte d'extension : seulement si l'extension est publique.
		return openExtensions.has(card.extension)
	})

	const rankOf = (collectionId) => {
		const found = collections.find((c) => c.id === collectionId)
		return found ? (found.number || 0) : 0
	}

	return kept.sort((a, b) => {
		const byCollection = rankOf(a.collection) - rankOf(b.collection)
		if (byCollection !== 0) return byCollection
		if (a.collection !== b.collection) return a.collection.localeCompare(b.collection)
		return compareCards(a, b)
	})
}

/**
 * Combien de cartes un booster Full contient aujourd'hui.
 *
 * AUJOURD'HUI, et c'est le point : ajouter une carte à la collection agrandit
 * le booster. Le nombre écrit dans le document n'est qu'une photographie prise
 * au dernier enregistrement — ce qui est distribué est toujours recompté ici.
 */
export function fullBoosterSize(booster, catalogue) {
	return fullBoosterCards(booster, catalogue).length
}

export default {
	isFullBooster,
	targetedCollectionIds,
	fullExtensionIds,
	fullBoosterCards,
	fullBoosterSize,
}
